import React, { useMemo } from "react";
import Select, { components, OptionProps, ValueContainerProps } from "react-select";

export type Option = { label: string; value: string | number };

type Props = {
  label?: string;
  options?: Option[];
  value: Option[]; 
  onChange: (v: Option[]) => void;
  defaultValue?: Option[];
  isDisabled?: boolean;
  className?: string;
  placeholder?: string;
  menuPortalTarget?: HTMLElement | null;
  required?: boolean;
};

const customStyles = {
  multiValueRemove: (provided: any) => ({ ...provided, display: "none" }),
  multiValue: (provided: any) => ({
    ...provided,
    textOverflow: "ellipsis",
    maxWidth: "100px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    display: "initial",
    backgroundColor: "transparent",
    padding: "0px",
  }),
  option: (provided: any) => ({
    ...provided,
    padding: "6px 8px",
    color: "inherit",
    backgroundColor: "white",
    fontSize: "13px",
    display: "flex",
    alignItems: "center",
    gap: 8,
  }),
  control: (provided: any, state: any) => ({
    ...provided,
    background: state.isDisabled ? "#FAFAFA" : "#fff",
    borderColor: state.isFocused ? "#6366F1" : "#d1d5db",
    minHeight: "34px",
    height: "34px",
    boxShadow: state.isFocused ? `0 0 0 3px ${"rgba(99,102,241,0.08)"}` : provided.boxShadow,
    paddingRight: 8,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    fontSize: "13px",
    height: "34px",
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: "100%",
  }),
  input: (provided: any) => ({ ...provided, margin: "0px" }),
  indicatorSeparator: (provided: any) => ({
    ...provided,
    width: "1px",
    backgroundColor: "#d1d5db",
    alignSelf: "stretch",
    marginLeft: 6,
    marginRight: 6,
    opacity: 1,
  }),
  indicatorsContainer: (provided: any) => ({ ...provided, height: "34px", margin: "0px" }),
  menu: (provided: any) => ({ ...provided, zIndex: 9999 }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

const CheckboxOption = (props: OptionProps<Option, true>) => {
  return (
    <components.Option {...props}>
      <input type="checkbox" readOnly checked={props.isSelected} style={{ marginRight: 8 }} />
      <span>{props.label}</span>
    </components.Option>
  );
};

const CustomValueContainer = (props: ValueContainerProps<Option, true>) => {
  const { children, ...rest } = props;
  let [values, input] = children as any;

  if (Array.isArray(values)) {
    values = values.length > 1 ? `Selected (${values.length})` : values;
  }

  return (
    <components.ValueContainer {...rest}>
      {values}
      {input}
    </components.ValueContainer>
  );
};

const MultiSelectInputDashboard: React.FC<Props> = ({
  label,
  options = [],
  value,
  onChange,
  defaultValue,
  isDisabled = false,
  className = "",
  placeholder = "Select...",
  menuPortalTarget = typeof document !== "undefined" ? document.body : null,
  required = false,
}) => {

  const orderedOptions = useMemo(() => {
    if (!options || options.length === 0) return options;

    const toKey = (v: any) => String(v?.value ?? v);
    const isOption = (v: any): v is Option =>
      v && typeof v === "object" && "label" in v && "value" in v;

    const buildOptionMap = (opts: Option[]) => {
      const map = new Map<string, Option>();
      for (const o of opts) map.set(String(o.value), o);
      return map;
    };

    const collectSelectedBucket = (val: any, optionMap: Map<string, Option>) => {
      const seen = new Set<string>();
      const bucket: Option[] = [];

      if (!Array.isArray(val) || val.length === 0) return { bucket, seen };

      for (const v of val) {
        const key = toKey(v);
        if (seen.has(key)) continue;
        seen.add(key);

        const matched = optionMap.get(key);
        if (matched) {
          bucket.push(matched);
        } else {
          const safe: Option = isOption(v) ? v : { label: String(v), value: key };
          bucket.push(safe);
        }
      }

      return { bucket, seen };
    };

    const collectOtherBucket = (opts: Option[], seen: Set<string>) => {
      const other: Option[] = [];
      for (const o of opts) {
        const key = String(o.value);
        if (!seen.has(key)) other.push(o);
      }
      return other;
    };

    const optionMap = buildOptionMap(options);
    const { bucket: selectedBucket, seen: selectedValuesSeen } = collectSelectedBucket(
      value,
      optionMap
    );

    if (selectedBucket.length === 0) return options;

    const otherBucket = collectOtherBucket(options, selectedValuesSeen);
    return [...selectedBucket, ...otherBucket];
  }, [options, value]);

  return (
    <div className={`space-y-1 text-xs ${className}`}>
      {label ? (
        <label className="block text-xs font-medium text-gray-700">
          {required && <span className="text-red-500 mr-1">*</span>}
          {label}
        </label>
      ) : null}

      <Select<Option, true>
        options={orderedOptions}
        value={value}
        defaultValue={defaultValue}
        onChange={(v) => onChange(((v ?? []) as Option[]) || [])}
        isMulti
        isSearchable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        styles={customStyles}
        components={{ Option: CheckboxOption, ValueContainer: CustomValueContainer }}
        isDisabled={isDisabled}
        placeholder={placeholder}
        menuPortalTarget={menuPortalTarget}
        tabSelectsValue={false}
      />
    </div>
  );
};

export default MultiSelectInputDashboard;
